import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { Json, type JsonObject } from "@tento-lona/sheets";

import type { PreferencesStorage } from "./preferences-storage.ts";

/**
 * Boundary readers for locally stored preferences. Storage is user-editable
 * and outlives schema changes, so every stored value is parsed and checked
 * here. A value that is present but unusable is logged
 * (`Tracing.warn("ui")`, with its storage key) and read as `null`; the
 * caller picks its default. Nothing is dropped silently.
 */
export namespace StoredPreference {
  /** Parse the JSON stored at `key`; `null` when absent or unparsable. */
  export function json(
    storage: PreferencesStorage,
    key: string,
  ): Option<Json> {
    const raw = storage.getItem(key);
    if (raw == null || raw.length === 0) return null;
    try {
      // `Json.wire` reads the parsed value into `Json` (for parsed text it
      // is a copy of the same value) — no cast of what the storage held.
      return Json.wire(JSON.parse(raw));
    } catch (err) {
      unusable(key, "not JSON", String(err));
      return null;
    }
  }

  /** The value when it is one of `allowed`; `null` (logged) otherwise. */
  export function oneOf<T extends string>(
    key: string,
    value: Option<Json>,
    allowed: readonly T[],
  ): Option<T> {
    if (value == null) return null;
    const match = allowed.find((candidate) => candidate === value);
    if (match != null) return match;
    unusable(key, `not one of ${allowed.join(" | ")}`, JSON.stringify(value));
    return null;
  }

  /** The stored string at `key` when it is one of `allowed`. */
  export function enumAt<T extends string>(
    storage: PreferencesStorage,
    key: string,
    allowed: readonly T[],
  ): Option<T> {
    return oneOf(key, storage.getItem(key), allowed);
  }

  /** The value as a JSON object; `null` (logged) when it is anything else. */
  export function record(
    key: string,
    value: Option<Json>,
  ): Option<JsonObject> {
    if (value == null) return null;
    if (Json.isObject(value)) return value;
    unusable(key, "not an object", JSON.stringify(value));
    return null;
  }

  /** The value as a JSON array; `null` (logged) when it is anything else. */
  export function array(
    key: string,
    value: Option<Json>,
  ): Option<readonly Json[]> {
    if (value == null) return null;
    if (Array.isArray(value)) return value;
    unusable(key, "not an array", JSON.stringify(value));
    return null;
  }

  /** The value as a finite number; `null` (logged) when it is anything else. */
  export function finite(key: string, value: Option<Json>): Option<number> {
    if (value == null) return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    unusable(key, "not a finite number", JSON.stringify(value));
    return null;
  }

  /** Log one unusable stored value (also for entries dropped from a list). */
  export function unusable(key: string, problem: string, detail: string): void {
    Tracing.warn("ui")("preferences/unusable-value", { key, problem, detail });
  }
}
